import * as zipjs from "@zip.js/zip.js";
import shpjs from "shpjs";
import bbox from "@turf/bbox";
import { EXTENSION_MIME, extOf } from "../../../utils/fileAcceptance";
import {
  type ShapefileGeometry,
  GeometryFileType,
  ProcessedGeometry,
  isUploadedFile,
  isProcessedFileUploaded,
} from "../../types";
import { generateUniqueFileIdentifier } from "../../uploadMachine";

export async function getBlobFromZipEntry(entry?: zipjs.Entry) {
  if (entry && entry.directory === false) {
    try {
      const mimeType =
        EXTENSION_MIME[extOf(entry.filename)] || "application/octet-stream";
      const blob = await entry.getData(new zipjs.BlobWriter(mimeType));

      if (!blob || blob.size === 0) {
        throw new Error(
          `Failed to extract data from ${entry.filename}: empty or invalid blob`
        );
      }

      return new File(
        [blob],
        entry.filename.split("/").pop() ?? entry.filename,
        {
          type: mimeType,
          lastModified: Number(entry.lastModDate),
        }
      );
    } catch (error) {
      throw new Error(
        `Failed to extract ${entry.filename}: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  return undefined;
}

export async function extractShapefileFromZip(
  zipFile: File,
  name: string
): Promise<File[]> {
  let reader: zipjs.ZipReader<zipjs.Entry> | null = null;

  try {
    reader = new zipjs.ZipReader(new zipjs.BlobReader(zipFile));
    const entries = await reader.getEntries();

    if (!entries || entries.length === 0) {
      throw new Error(
        `ZIP file ${zipFile.name} appears to be empty or corrupted`
      );
    }

    const shapefileEntries = {
      shp: entries.find(
        (entry) =>
          entry.filename.toLowerCase().endsWith(`${name.toLowerCase()}.shp`) &&
          !entry.filename.includes("__MACOSX")
      ),
      shx: entries.find(
        (entry) =>
          entry.filename.toLowerCase().endsWith(`${name.toLowerCase()}.shx`) &&
          !entry.filename.includes("__MACOSX")
      ),
      dbf: entries.find(
        (entry) =>
          entry.filename.toLowerCase().endsWith(`${name.toLowerCase()}.dbf`) &&
          !entry.filename.includes("__MACOSX")
      ),
      prj: entries.find(
        (entry) =>
          entry.filename.toLowerCase().endsWith(`${name.toLowerCase()}.prj`) &&
          !entry.filename.includes("__MACOSX")
      ),
    };

    if (!shapefileEntries.shp) {
      throw new Error(
        `Required shapefile component ${name}.shp not found in ZIP file`
      );
    }

    const [shp, shx, dbf, prj] = await Promise.all([
      getBlobFromZipEntry(shapefileEntries.shp),
      getBlobFromZipEntry(shapefileEntries.shx),
      getBlobFromZipEntry(shapefileEntries.dbf),
      getBlobFromZipEntry(shapefileEntries.prj),
    ]);

    const extractedFiles = [shp, shx, dbf, prj].filter(
      (f): f is File => f !== undefined
    );

    if (extractedFiles.length === 0) {
      throw new Error(`Failed to extract any valid files from ZIP archive`);
    }

    if (!shp) {
      throw new Error(
        `Failed to extract required shapefile component ${name}.shp`
      );
    }

    return extractedFiles;
  } catch (error) {
    throw new Error(
      `Failed to extract shapefile from ${zipFile.name}: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  } finally {
    if (reader) {
      await reader.close();
    }
  }
}

export function gatherShapefileComponents(
  name: string,
  inputFiles: File[]
): File[] {
  const components = {
    shx: inputFiles.find(
      (f) => f.name.toLowerCase() === `${name.toLowerCase()}.shx`
    ),
    dbf: inputFiles.find(
      (f) => f.name.toLowerCase() === `${name.toLowerCase()}.dbf`
    ),
    prj: inputFiles.find(
      (f) => f.name.toLowerCase() === `${name.toLowerCase()}.prj`
    ),
  };

  return [components.shx, components.dbf, components.prj].filter(
    (f): f is File => f !== undefined
  );
}

export function createShapefileUpload(
  shpFile: File,
  name: string,
  componentFiles: File[]
): ShapefileGeometry {
  const id = generateUniqueFileIdentifier(shpFile).name;

  const shxFile = componentFiles.find(
    (f) => f.name.toLowerCase() === `${name.toLowerCase()}.shx`
  );
  const dbfFile = componentFiles.find(
    (f) => f.name.toLowerCase() === `${name.toLowerCase()}.dbf`
  );
  const prjFile = componentFiles.find(
    (f) => f.name.toLowerCase() === `${name.toLowerCase()}.prj`
  );

  const upload = {
    id,
    file: shpFile,
    name,
    type: "shp",
    componentKeys: {
      shp: id,
      shx: shxFile ? generateUniqueFileIdentifier(shxFile).name : undefined,
      dbf: dbfFile ? generateUniqueFileIdentifier(dbfFile).name : undefined,
      prj: prjFile ? generateUniqueFileIdentifier(prjFile).name : undefined,
    },
    files: {
      shp: shpFile,
      shx: shxFile,
      dbf: dbfFile,
      prj: prjFile,
    },
    progress: 0,
  } as ShapefileGeometry;

  return {
    ...upload,
    missing: computeMissingFlag(upload),
  };
}

export function updateShapefileComponent(
  existingShapefile: ShapefileGeometry,
  componentFile: File,
  extension: GeometryFileType
): ShapefileGeometry {
  const updatedFiles = {
    ...existingShapefile.files,
    [extension]: componentFile,
  };

  const componentKey = generateUniqueFileIdentifier(componentFile).name;

  const updated = {
    ...existingShapefile,
    files: updatedFiles,
    componentKeys: {
      ...existingShapefile.componentKeys,
      [extension]: componentKey,
    },
    ...(updatedFiles.shp instanceof File &&
      updatedFiles.prj instanceof File && {
        error: undefined,
        data: undefined,
      }),
  };

  return {
    ...updated,
    missing: computeMissingFlag(updated),
  };
}

export function addOrUpdateShapefileInOutput(
  output: ProcessedGeometry[],
  shapefile: ShapefileGeometry,
  componentFile: File,
  extension: GeometryFileType
): void {
  const existingIndex = output.findIndex(
    (upload) => upload.id === shapefile.id
  );

  if (existingIndex === -1) {
    const updated = updateShapefileComponent(
      shapefile,
      componentFile,
      extension
    );
    output.push(updated);
  } else {
    output[existingIndex] = updateShapefileComponent(
      output[existingIndex] as ShapefileGeometry,
      componentFile,
      extension
    );
  }
}

export function shouldUpdateExistingShapefile(
  existingShapefile: ShapefileGeometry | undefined,
  extension: GeometryFileType
): existingShapefile is ShapefileGeometry {
  if (!existingShapefile) return false;

  if (!["shp", "shx", "dbf", "prj"].includes(extension)) return false;

  const existingFile =
    existingShapefile.files[extension as "shp" | "shx" | "dbf" | "prj"];
  return !(existingFile instanceof File) && !isUploadedFile(existingFile);
}

export async function readShapefile({
  input,
}: {
  input: { shp?: File; dbf?: File; shx?: File; prj?: File };
}) {
  try {
    if (!input.shp) {
      throw new Error("Required .shp file is missing");
    }

    const shp = await input.shp.arrayBuffer();
    const dbf = await input.dbf?.arrayBuffer();
    const shx = await input.shx?.arrayBuffer();
    const prj = await input.prj?.arrayBuffer();

    if (!shp || shp.byteLength === 0) {
      throw new Error("Shapefile (.shp) is empty or corrupted");
    }

    let collection: GeoJSON.FeatureCollection;

    try {
      collection = (await shpjs({
        shp: shp,
        dbf: dbf,
        shx: shx,
        prj: prj,
      } as any)) as GeoJSON.FeatureCollection;
    } catch (parseError) {
      throw new Error(
        `Failed to parse shapefile: ${
          parseError instanceof Error ? parseError.message : String(parseError)
        }`
      );
    }

    if (
      !collection ||
      !collection.type ||
      collection.type !== "FeatureCollection"
    ) {
      throw new Error("Shapefile parsing resulted in invalid GeoJSON");
    }

    if (!Array.isArray(collection.features)) {
      throw new Error(
        "Shapefile parsing resulted in GeoJSON with invalid features array"
      );
    }

    return {
      collection,
      bbox: shp && prj ? bbox(collection) : undefined,
    };
  } catch (error) {
    throw new Error(
      `Failed to read shapefile: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
}

export function computeMissingFlag(upload: ProcessedGeometry): boolean {
  if (upload.type === "shp") {
    const shpUploaded =
      upload.files.shp instanceof File || isUploadedFile(upload.files.shp);
    const prjUploaded =
      upload.files.prj instanceof File || isUploadedFile(upload.files.prj);

    return !shpUploaded || !prjUploaded;
  } else {
    if (upload.type === "geojson") {
      return !(
        upload.files.geojson instanceof File ||
        isUploadedFile(upload.files.geojson)
      );
    } else if (upload.type === "kml") {
      return !(
        upload.files.kml instanceof File || isUploadedFile(upload.files.kml)
      );
    } else if (upload.type === "kmz") {
      return !(
        upload.files.kmz instanceof File || isUploadedFile(upload.files.kmz)
      );
    } else if (upload.type === "csv") {
      return !(
        upload.files.csv instanceof File || isUploadedFile(upload.files.csv)
      );
    } else if (upload.type === "pdf") {
      return !(
        upload.files.pdf instanceof File || isUploadedFile(upload.files.pdf)
      );
    } else if (upload.type === "xlsx") {
      return !(
        upload.files.xlsx instanceof File || isUploadedFile(upload.files.xlsx)
      );
    }
    return true;
  }
}
